// mds-dojo — dojo/_base/declare + the composition runtime that replaces the AMD
// `declare` for our native-class world.
//
// Three exports:
//   * compose(Spine, ...MixinFactories)  — build a C3-linearized native class
//     chain. Multi-base `@declare(A, B, C)` becomes `extends compose(A, B, C)`,
//     i.e. C(B(A)): the RIGHTMOST base is the OUTERMOST wrapper, so it sits
//     closest to the leaf and `super` walks base→…→leaf exactly like dojo's C3
//     MRO (and a base shared by two branches appears once — diamond dedup).
//   * mdsWidget(Ctor) — wrap a widget class in a construct-trap Proxy that runs
//     the dijit create() pipeline ONCE, after the whole native constructor chain
//     finishes (guard: newTarget === this proxy). This is what lets modern
//     ES-class widgets (whose field initializers are hoisted to the prototype)
//     run the postMixInProperties→buildRendering→postCreate sequence with their
//     defaults + constructor bodies already in place — the fix for the
//     "declare calls constructors without new / native classes throw" trap.
//   * declare(className?, bases, props) — CLASSIC object-literal form, kept for
//     legacy hand-written widgets (more/_WidgetBaseLite.js, more/Legacy/*.js).
//     Produces a native class from `bases` + `props`, with a `this.inherited()`
//     that walks the prototype chain (no arguments.callee — illegal in ESM),
//     interoperating with native `super`-based subclasses.

import faithfulDeclare, { setOnDeclare } from './declare-runtime';

export type Ctor = new (...args: any[]) => any;
export type MixinFactory = <T extends Ctor>(Base: T) => Ctor;

// --- declared-name registry --------------------------------------------------
// Real dojo declare(className, ...) registers the ctor under its dotted name
// (lang.setObject(className, ctor)) — that's how a dotted data-dojo-type like
// "more.HintBox" (or a secondary class that has no module of its own, e.g.
// "more.MultiSelectBox") resolves in the parser. We keep a flat registry the
// parser consults as a fallback, and ALSO mirror onto the global object path
// for lang.getObject-style lookups in legacy code.

/**
 * **mds-dojo** — flat registry of widget constructors keyed by their dotted
 * declared name (e.g. `"more.HintBox"`, `"more.MultiSelectBox"`). The parser
 * consults this as a fallback so a dotted `data-dojo-type` resolves even when the
 * class has no module of its own. Filled by {@link registerDeclaredType}.
 *
 * NOT a vanilla Dojo 1.x export — real dojo resolved dotted names through the AMD
 * loader / `lang.getObject`; this is the mds stand-in for that.
 */
export const declaredTypes: Record<string, Ctor> = {};

/**
 * **mds-dojo** — register `ctor` under its dotted declared `name` so a dotted
 * `data-dojo-type` resolves (dojo's `lang.setObject(className, ctor)` parity), and
 * mirror it onto the global object path for `lang.getObject`-style legacy lookups.
 *
 * FIRST registration wins: the eager glob's (alphabetical) load order is arbitrary,
 * unlike dojo's dep-graph order, so a stale legacy twin must not clobber the
 * maintained widget that already claimed the name.
 *
 * NOT a vanilla Dojo 1.x export.
 * @param name dotted declared class name, or a module id (ids skip the global-path mirror).
 * @param ctor the constructor to register.
 */
export function registerDeclaredType(name: string, ctor: Ctor): void {
  // FIRST registration wins: the eager glob's (alphabetical) order would let a
  // stale legacy twin re-declare a dotted name AFTER the maintained widget
  // registered it. dojo was last-wins, but its load order was the dep graph —
  // ours is arbitrary, so keep the first (maintained widgets sort first here).
  if (name in declaredTypes) { return; }
  declaredTypes[name] = ctor;
  if (name.indexOf('/') !== -1) { return; } // module ids: registry-only (no global path)
  // Mirror dojo's lang.setObject(className, ctor) global-path side effect.
  try {
    const parts = name.split('.');
    let obj: any = (globalThis as any);
    for (let i = 0; i < parts.length - 1; i++) {
      obj = obj[parts[i]] = obj[parts[i]] || {};
    }
    obj[parts[parts.length - 1]] = ctor;
  } catch (e) { /* non-fatal */ }
}

// --- compose ---------------------------------------------------------------

// --- mixin-factory protocol --------------------------------------------------
// A class used as a 2nd+ @declare base is emitted as a FACTORY (Base)=>class.
// Many of those classes are DUAL-ROLE: they also appear as the FIRST base
// elsewhere (`@declare(_StatefulSection)` in 80+ editors while _StatefulSection
// is itself a mixin somewhere else). dojo's C3 keeps the leading base's own
// chain, so a factory in SPINE position must expand to "its own declared bases
// + itself" — markMixinFactory records those bases and compose builds (and
// caches) that spine class on first use.

/**
 * **mds-dojo** — tag a `(Base) => class extends Base` mixin factory as a dual-role
 * declare mixin and record the bases it needs when used in SPINE (first-base)
 * position. Emitted by the codemod for classes that appear both as a mixin AND as a
 * leading base; you rarely call this by hand.
 *
 * NOT a vanilla Dojo 1.x export — this is internal glue for {@link compose}'s C3
 * expansion of factory-shaped mixins.
 * @param factory the mixin factory.
 * @param spineBases the factory's own declared bases, which {@link compose} expands
 *   ("its own chain + itself") when the factory is used as the first base.
 */
export function markMixinFactory<F>(factory: F, spineBases: any[] = []): F {
  (factory as any).__mdsMixinFactory = true;
  (factory as any).__mdsSpineBases = spineBases;
  return factory;
}

// instanceof — dojo's isInstanceOf "is-a" semantics for the native-class world.
//
// A dual-role class is emitted as a FACTORY (Base)=>class; the codemod names the
// INNER class the SAME as the factory, so inside a method `x instanceof Name`
// binds to THAT produced class, NOT the factory. Every compose() builds a
// DIFFERENT produced class from the factory, so native `instanceof <producedA>`
// is false for an instance of sibling <producedB> — a nested `___root` section
// built by one compose() then fails `instanceof _StatefulSection` inside a method
// closed over another, and setData distribution to it silently no-ops.
//
// Fix: brand every instance a factory produces, and make BOTH the factory AND its
// produced classes answer instanceof via that brand. The subtlety is inheritance:
// `Symbol.hasInstance` on a class is inherited by every subclass, so a brand check
// must be `this`-based (read the brand off the class instanceof is invoked on) —
// a fixed-capture check would make e.g. `x instanceof _StatefulBase` true for any
// widget (it would inherit a base mixin's brand). Real widget classes get an OWN
// `this`-based NATIVE check from mdsWidget() so they never answer by an inherited
// brand. Factory functions are never extended, so their brand check is safe.
function brandFactory(M: any, produced: Ctor): void {
  if (!M.__mdsBrand) {
    M.__mdsBrand = Symbol('mdsMixin');
    if (!Object.getOwnPropertyDescriptor(M, Symbol.hasInstance)) {
      Object.defineProperty(M, Symbol.hasInstance, {
        value: (inst: any) => !!(inst && inst[M.__mdsBrand]),
      });
    }
  }
  (produced as any).prototype[M.__mdsBrand] = true;
  // Own static so subclasses don't answer by THIS class's brand (they set their
  // own via their own brandFactory pass, or get mdsWidget's native check).
  (produced as any).__mdsFactoryBrand = M.__mdsBrand;
  if (!Object.getOwnPropertyDescriptor(produced, Symbol.hasInstance)) {
    Object.defineProperty(produced, Symbol.hasInstance, {
      value: function (this: any, inst: any) { return !!(inst && this.__mdsFactoryBrand && inst[this.__mdsFactoryBrand]); },
    });
  }
}

// dojo isInstanceOf parity for a concrete (mdsWidget-wrapped) class: an OWN,
// `this`-based NATIVE prototype-chain check. Own so it wins over any brand
// `Symbol.hasInstance` inherited from a branded produced ancestor (which would
// otherwise make `x instanceof ThisWidget` true for unrelated brand carriers);
// `this`-based so subclasses that inherit it still test THEIR own prototype.
function defineNativeHasInstance(Ctor: any): void {
  if (Object.getOwnPropertyDescriptor(Ctor, Symbol.hasInstance)) { return; }
  Object.defineProperty(Ctor, Symbol.hasInstance, {
    value: function (this: any, inst: any) {
      const proto = this && this.prototype;
      if (!proto) { return false; }
      for (let o = inst; o != null; o = Object.getPrototypeOf(o)) {
        if (o === proto) { return true; }
      }
      return false;
    },
  });
}

function spineOf(x: any): Ctor {
  if (typeof x === 'function' && x.__mdsMixinFactory) {
    if (!x.__mdsSpineClass) {
      const bases = x.__mdsSpineBases || [];
      x.__mdsSpineClass = x(bases.length ? (compose as any)(...bases) : (class {} as any));
      brandFactory(x, x.__mdsSpineClass);
    }
    return x.__mdsSpineClass;
  }
  return x;
}

// Is declare-mixin M already present in native Base's prototype chain? dojo C3 keeps
// each class once, so compose() must not re-add a mixin the base already carries
// (e.g. compose(_WidgetBaseSP, _TemplatedMixin) — _WidgetBaseSP already contains it).
// Base is a NATIVE class, so walk its prototype chain: each native level may carry its
// own `declaredClass`; each faithful-declare level exposes its full MRO via
// `constructor._meta.bases` (where mixed-in classes live, not as separate proto levels).
function mixinInChain(Base: any, M: any): boolean {
  const dc = M && M.prototype && M.prototype.declaredClass;
  for (let p = Base && Base.prototype; p && p !== Object.prototype; p = Object.getPrototypeOf(p)) {
    if (dc && Object.prototype.hasOwnProperty.call(p, 'declaredClass') && p.declaredClass === dc) { return true; }
    const ctor = Object.prototype.hasOwnProperty.call(p, 'constructor') ? p.constructor : null;
    const bases = ctor && Object.prototype.hasOwnProperty.call(ctor, '_meta') && ctor._meta.bases;
    if (bases) {
      if (bases.indexOf(M) !== -1) { return true; }
      if (dc) { for (const b of bases) { if (b && b.prototype && b.prototype.declaredClass === dc) { return true; } } }
    }
  }
  return false;
}

// --- typed compose return --------------------------------------------------
// Surface EVERY base's instance members on `class X extends compose(...)` so a
// hand-authored widget needs NO `interface X extends InstanceType<...>`
// declaration-merge line (the codemod still emits it; both shapes coexist).
// Each compose() arg is a class/ctor spine OR a mixin factory `(base)=>class`;
// map each to the instance type it contributes and INTERSECT them. Because the
// result is an INTERSECTION (A & B), a DIAMOND (both bases derive _WidgetBase)
// does NOT collide the way `interface X extends A, B` does (A & A = A) — so the
// heritage reduction the codemod needed for FramedInput-style diamonds is gone.
//
// Emitting a CONCRETE construct signature (never bare `any`) is also what dodges
// the zero-arg-ctor synthesis bug the old `NotAny` guard existed to prevent, so
// NotAny is no longer needed. A legacy JS `any` spine maps to a permissive record
// (never `{}`) so loose member access on untyped bases keeps compiling.
type IsAny<T> = 0 extends (1 & T) ? true : false;
// Exported so declare-runtime.ts can reuse the exact same base-list→instance mapping
// for its typed `declare()` overloads (via `import type`, erased at runtime — no cycle).
/**
 * **mds-dojo** type util — the instance type that a single {@link compose} / typed
 * {@link declare} base contributes: a class ctor → its instance, a `(Base)=>class`
 * mixin factory → its produced instance, a legacy `any` spine → a permissive record.
 */
export type BaseInstance<X> =
    IsAny<X> extends true ? Record<string, any>
  : X extends abstract new (...a: any[]) => infer I ? I
  : X extends (base: any) => infer R ? (R extends abstract new (...a: any[]) => infer I ? I : {})
  : {};
/**
 * **mds-dojo** type util — the INTERSECTION of every base's {@link BaseInstance} in
 * a base list. This is the self-type of `class X extends compose(...)`, which is why
 * a hand-authored widget needs no `interface X extends …` declaration-merge line.
 */
export type ComposedInstance<T extends readonly any[]> =
    T extends readonly [infer H, ...infer R] ? BaseInstance<H> & ComposedInstance<R> : {};
type Composed<T extends readonly any[]> = { new (params?: any, srcNodeRef?: any): ComposedInstance<T> };
/** **mds-dojo** — zero-base `compose()` → an empty native base (a `@declare()` with no bases). See the variadic overload for details. */
export function compose(): Ctor;
/**
 * **mds-dojo** — build a C3-linearized *native* class to `extends` from. This is the
 * mds replacement for a multi-base Dojo `declare([A, B, C], {…})`; it is NOT a
 * vanilla Dojo 1.x export.
 *
 * ```ts
 * class MyWidget extends compose(_WidgetBaseLite, SomeMixin) { … }
 * export default mdsWidget(MyWidget);
 * ```
 *
 * MRO: the bases form the dojo C3 chain — the RIGHTMOST base is the OUTERMOST
 * wrapper (closest to the leaf), so `super.foo()` walks base → … → leaf exactly like
 * dojo's C3, and a base shared by two branches appears once (diamond dedup).
 *
 * Typing: the return is the INTERSECTION of every base's instance type, so the
 * subclass self-types — you get autocomplete + typo-catching on inherited members
 * with NO `interface MyWidget extends …` merge line. Being an intersection (`A & B`),
 * a diamond does not collide the way `interface X extends A, B` would.
 *
 * Always pair with {@link mdsWidget} on the class you export, so the dijit `create()`
 * lifecycle runs.
 *
 * @param bases spine class first, then mixin classes / factories, left → right.
 * @returns a native class constructor typed as the intersection of all base instances.
 */
export function compose<T extends readonly any[]>(...bases: T): Composed<T>;
export function compose(spine?: any, ...mixins: any[]): Ctor {
  // Zero-base `@declare()` widgets compose over an empty base.
  const base: Ctor = spine ? spineOf(spine) : (class {} as any);
  return mixins.reduce<Ctor>((Base, M) => {
    // FAITHFUL declare-class mixin (dijit/* ported via declare-runtime — has _meta):
    // mix it in through the faithful C3 declare. This gives real dojo MRO semantics
    // AND dedups a re-listed mixin natively (dojo c3mro has each class exactly once)
    // — the correct, general fix for the "re-listed mixin runs twice" bug (was C18,
    // hand-fixed via brands for the factory path). declare([Base, M]) works whenever
    // Base is itself a declare-class/plain-ctor (the app composes dijit mixins over
    // _WidgetBase(Lite), both declare) — the common case.
    if (M && (M as any)._meta) {
      // Base is itself a faithful declare-class (OWN _meta): c3mro handles it — real
      // dojo MRO + native dedup of a re-listed mixin (common case; also the old C18).
      // NB: OWN _meta only — a native app class (`class X extends compose(...)`) INHERITS
      // the static `_meta` from its faithful-declare ancestor, so `Base._meta` is truthy
      // for it; hasOwnProperty distinguishes a real declare-class from a native subclass.
      if (Object.prototype.hasOwnProperty.call(Base, '_meta')) {
        return (faithfulDeclare as any)([Base, M]) as Ctor;
      }
      // Base is a NATIVE ES class (an app widget base like _WidgetBaseSP / _ControlBase,
      // no _meta). c3mro sees it as a FLAT single base and can't walk its native
      // prototype chain, so faithfulDeclare([Base, M]) DROPS the base's inherited
      // methods (Crash B: `this.UiModder is undefined` — _WidgetBaseSP.postMixInProperties
      // vanished). Keep the base's full chain via native `extends`. Dedup a mixin already
      // in the chain (dojo C3 has each class once — e.g. compose(_WidgetBaseSP,
      // _TemplatedMixin) where _WidgetBaseSP already contains _TemplatedMixin); otherwise
      // layer the mixin's OWN prototype methods onto a native subclass.
      if (mixinInChain(Base, M)) { return Base; }
      const Layered: any = class extends (Base as any) { };
      const layerProto = (M as any).prototype;
      for (const n of Object.getOwnPropertyNames(layerProto)) {
        if (n === 'constructor') { continue; }
        if (!Object.prototype.hasOwnProperty.call(Layered.prototype, n)) {
          Object.defineProperty(Layered.prototype, n, Object.getOwnPropertyDescriptor(layerProto, n) as PropertyDescriptor);
        }
      }
      return Layered as Ctor;
    }
    // MIXIN FACTORY path (app-level mixins emitted by the codemod). Brand-based
    // diamond dedup: skip a factory whose output is already in Base's chain.
    if ((M as any).__mdsMixinFactory) {
      const brand = (M as any).__mdsBrand;
      if (brand && Base.prototype && Base.prototype[brand]) { return Base; }
      const produced = M(Base) as Ctor;
      brandFactory(M, produced);
      return produced;
    }
    // PLAIN CONSTRUCTOR mixin — a faithful dojo base authored as a plain ctor with
    // its API on the prototype (e.g. dojo/Evented: on/emit), NOT a codemod factory
    // (no __mdsMixinFactory) and NOT a declare-class (no _meta). Detect it by an OWN
    // prototype member beyond `constructor` (a `(Base)=>class` factory's own
    // prototype has only `constructor`; an arrow factory has no prototype).
    //
    // Layer its prototype members onto a NATIVE-ES subclass of Base — exactly how
    // the lite Evented mixin FACTORY worked before the faithful port, and matching
    // the native-ES output of the __mdsMixinFactory path above. This keeps the whole
    // composed chain native-ES, so `class _WidgetBaseSP extends compose(...)` + its
    // native `super()` walks a native prototype chain. (Routing the plain ctor
    // through faithful declare instead would put dojo's function-ctor c3mro chaining
    // under a native super() — its constructor loop `.apply()`s bases, which throws
    // "class constructors must be invoked with 'new'" on a native-ES layer. That was
    // the bug.) Evented's own ctor is a no-op (state lives in on/emit); the default
    // subclass ctor forwards args to Base, so nothing is lost.
    const mProto = (M as any).prototype;
    if (mProto && Object.getOwnPropertyNames(mProto).some((n) => n !== 'constructor')) {
      const Mixed: any = class extends (Base as any) { };
      for (const n of Object.getOwnPropertyNames(mProto)) {
        if (n === 'constructor') { continue; }
        Object.defineProperty(Mixed.prototype, n, Object.getOwnPropertyDescriptor(mProto, n) as PropertyDescriptor);
      }
      return Mixed as Ctor;
    }
    // Unmarked mixin FACTORY (inline `(Base)=>class`): call it.
    return M(Base) as Ctor;
  }, base);
}

/**
 * **mds-dojo** — install lazy prototype defaults: each key constructs on FIRST
 * access and memoizes the value on the prototype (dojo's shared-prototype-value
 * semantics); assigning shadows per instance.
 *
 * The codemod routes hoisted `field = new SomeImportedClass()` initializers here instead
 * of `Object.assign`, because constructing widgets/objects at MODULE-EVAL time explodes
 * inside import cycles (TDZ) and forces load-order sensitivity. Only IMPORTED ctors land
 * here: a built-in-global default (`new Array<T>()`, `new Map()`, `new Date()`, …) has no
 * import and therefore no cycle to hazard, so the codemod leaves it as a plain per-instance
 * class field-initializer. Hand-authored clean-shape widgets use plain class field
 * initializers too — you rarely need this directly.
 *
 * NOT a vanilla Dojo 1.x export.
 * @param proto the prototype to define the lazy accessors on.
 * @param factories map of key → factory that produces the default value.
 */
export function mdsLazyProto(proto: any, factories: Record<string, () => any>): void {
  for (const k of Object.keys(factories)) {
    Object.defineProperty(proto, k, {
      configurable: true,
      get() {
        const v = factories[k]();
        Object.defineProperty(proto, k, { value: v, writable: true, configurable: true });
        return v;
      },
      set(v: any) {
        Object.defineProperty(this, k, { value: v, writable: true, configurable: true, enumerable: true });
      },
    });
  }
}

// --- mdsWidget (construct-trap) --------------------------------------------

/**
 * **mds-dojo** — wrap a widget class so the dijit `create()` lifecycle
 * (`postMixInProperties` → `buildRendering` → `postCreate`) runs exactly ONCE, AFTER
 * the whole native constructor chain finishes. NOT a vanilla Dojo 1.x export — this
 * is the shim that lets a modern ES-class widget run dojo's lifecycle.
 *
 * Every hand-authored widget's default export goes through this:
 * ```ts
 * class Counter extends compose(_WidgetBaseLite) { … }
 * export default mdsWidget(Counter);
 * ```
 *
 * Mechanism: a construct-trap `Proxy`. It runs the full native ctor chain first (so
 * class field initializers + ctor bodies are already in place), THEN calls the dijit
 * pipeline — but only for the OUTERMOST `new` (`newTarget === proxy`), so a nested
 * `super()` does not re-trigger it. The wrapper is also callable WITHOUT `new` (dojo
 * declare()-made ctors self-new, so `MyWidget(params)` still works).
 *
 * @param Ctor the widget class, typically `class X extends {@link compose}(…)`.
 * @param moduleId optional dojo-id to self-register under. Usually omit it — the
 *   app's `import.meta.glob` boot registers every widget by its build-time path id.
 * @returns the same class (same type), now lifecycle-managed and `instanceof`-safe.
 */
export function mdsWidget<T extends Ctor>(Ctor: T, moduleId?: string): T {
  const proxy: any = new Proxy(Ctor, {
    construct(target, args, newTarget) {
      const instance = Reflect.construct(target as any, args, newTarget);
      // Only the OUTERMOST `new` (newTarget === this proxy) triggers the
      // lifecycle. Nested super() calls carry a different newTarget and skip it.
      if (newTarget === proxy && typeof (instance as any).__create === 'function') {
        (instance as any).__create(args[0], args[1]);
      }
      return instance;
    },
    // dojo declare()-made constructors are callable WITHOUT `new` (they
    // self-new) — legacy call sites like `declare([...])(params)` rely on it.
    apply(_target, _thisArg, args) {
      return new proxy(...args);
    },
  });
  // Give the wrapped class an OWN native instanceof so `x instanceof ThisWidget`
  // never answers by a brand `Symbol.hasInstance` inherited from a branded
  // produced ancestor (`instanceof _StatefulBase` must NOT match every section).
  // `x instanceof proxy` forwards @@hasInstance to the target, so define it there.
  defineNativeHasInstance(Ctor);
  // Mark the wrapped class as mdsWidget-managed: the faithful dijit/_WidgetBase
  // (declare runtime) defers its dojo postscript→create to THIS construct-trap so
  // the lifecycle runs AFTER the full native constructor chain (incl. the app
  // widget's own field initializers), not mid-super(). See _WidgetBase.postscript.
  (Ctor as any).prototype.__mdsManaged = true;
  // Self-registration under the widget's MODULE ID (emitted by the codemod):
  // widgets constructed at module-eval time (prototype-hoisted `new Widget()`
  // defaults) parse their templates BEFORE the entry's glob loop runs — the
  // dojo loader had every dep registered by then, and so do we.
  if (moduleId) { registerDeclaredType(moduleId, proxy); }
  const declared = (Ctor as any).prototype && (Ctor as any).prototype.declaredClass;
  if (typeof declared === 'string' && declared) { registerDeclaredType(declared, proxy); }
  return proxy as T;
}

// --- classic declare() — now the FAITHFUL runtime -------------------------
// dojo/_base/declare's default export IS the faithful C3 declare (declare-runtime).
// This replaces the earlier compose-based classic declare (old __inhName inherited
// mechanism) so that legacy classic-declare widgets (more/_WidgetBaseLite.js,
// more/Legacy/*.js) run on the SAME runtime as the ported dijit base chain — a
// mixed mechanism broke this.inherited across the boundary. The app's codemodded
// widgets use the named compose()/mdsWidget() exports (below), unaffected.
//
// Wire declared class-name registration into the mds parser registry (dojo did
// lang.setObject; the faithful runtime also calls this hook) so dotted
// data-dojo-type ids resolve.
setOnDeclare(registerDeclaredType);
/**
 * **mds-dojo** — the faithful C3 `declare()` runtime, re-exported. Same call shapes
 * as vanilla Dojo `declare(className?, superclass, props)`, but this is the mds
 * re-implementation: it produces a native-class-interoperable constructor with a
 * `this.inherited()` that walks the prototype chain (no `arguments.callee`), and it
 * has a TYPED return so subclasses see real member types instead of `any`.
 *
 * Kept for legacy hand-written / classic-declare widgets. NEW widgets should use the
 * native-class shape — `class X extends {@link compose}(…)` + {@link mdsWidget}.
 */
export const declare = faithfulDeclare;

const _default = declare as any;
_default.compose = compose;
_default.mdsWidget = mdsWidget;
_default.declaredTypes = declaredTypes;
_default.registerDeclaredType = registerDeclaredType;
_default.mdsLazyProto = mdsLazyProto;
export default _default;
